import { api } from "@/api/client";
import { useRemoteQuery } from "@/api/dataClient";
import type { AdminPageId } from "@/app/navigation";
import type { SearchRecord, ShellNotification, ShellUser } from "@/layout/PremiumAdminAppShell";

export interface AppShellPayload {
  brand: {
    name: string;
    product: string;
    apiVersion: string;
  };
  principal: {
    id: string;
    workspaceId: string;
    type: "USER" | "SERVICE_ACCOUNT";
    name: string;
    email?: string;
    roles: string[];
    permissions: string[];
  };
  navigation: Array<{
    key: AdminPageId;
    label: string;
    path: string;
    permission: string;
    section: string;
  }>;
  navigationSections: Record<string, Array<{ key: AdminPageId; label: string; path: string; permission: string; section: string }>>;
  notifications: Array<{
    id: string;
    type: "Work" | "Localization" | "Automation" | "Security" | "LiveDealz";
    title: string;
    detail: string;
    time: string;
    unread: boolean;
    pageId: AdminPageId;
    tone: "orange" | "green" | "red" | "blue" | "violet";
  }>;
  badges: {
    unreadInbox: number;
    openAssignedWork: number;
  };
  commands: Array<{
    id: string;
    label: string;
    path: string;
    keywords: string[];
  }>;
  capabilities: Record<string, boolean>;
}

export function useAppShell(enabled: boolean) {
  return useRemoteQuery<AppShellPayload>({
    queryKey: ["dashboard", "app-shell"],
    queryFn: ({ signal }) => api.request<AppShellPayload>('/dashboard/app-shell', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function mapPrincipalToShellUser(principal: AppShellPayload["principal"]): ShellUser {
  const initials = principal.name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return {
    id: principal.id,
    name: principal.name,
    initials: initials || "U",
    email: principal.email ?? "",
    title: principal.roles.join(" · ") || "Team member",
    status: "Available",
    countries: [],
    languages: [],
    assetLibrary: "My Asset Library",
  };
}

export function mapCommandsToSearchRecords(commands: AppShellPayload["commands"]): SearchRecord[] {
  return commands.map((cmd) => ({
    id: cmd.id,
    type: "Command",
    title: cmd.label,
    subtitle: cmd.path,
    pageId: (cmd.path.split("/").pop() ?? "command-center") as AdminPageId,
    icon: "command",
    keywords: cmd.keywords,
  }));
}

export function normalizeNotifications(notifications: AppShellPayload["notifications"]): ShellNotification[] {
  return notifications.map((n) => ({
    id: n.id,
    type: n.type,
    title: n.title,
    detail: n.detail,
    time: n.time,
    unread: n.unread,
    pageId: n.pageId,
    tone: n.tone,
  }));
}
